import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { scrollToId } from "../SmoothScroll";

const EASE = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "105%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const Badge = () => (
  <svg viewBox="0 0 200 200" className="w-28 h-28 sm:w-36 sm:h-36 spin-slow" aria-hidden="true">
    <defs><path id="circ" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
    <text fill="#D7263D" fontSize="12" letterSpacing="3" fontFamily="JetBrains Mono, monospace">
      <textPath href="#circ">MEMBERS ONLY • PRIVATE ACCESS • MEMBERS ONLY • </textPath>
    </text>
  </svg>
);

export function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 80, damping: 18 });
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 80, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
    ref.current.style.setProperty("--sx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--sy", `${e.clientY - r.top}px`);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden pt-[110px] pb-16"
      style={{ background: "radial-gradient(600px circle at var(--sx, 70%) var(--sy, 40%), rgba(215,38,61,0.10), transparent 60%)" }}>
      <div className="pointer-events-none absolute -bottom-10 left-0 right-0 select-none text-center font-display text-[26vw] leading-none text-outline opacity-20">KR</div>
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-6 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="eyebrow mb-8">
            ( Private membership — by invitation of Katie )
          </motion.p>
          <h1 data-testid="hero-title" className="font-display font-normal leading-[0.88] tracking-[-0.02em] text-[#0E1B3D] text-[17vw] sm:text-[7.5rem] lg:text-[9.5rem]">
            <MaskLine delay={0.3}>Katie</MaskLine>
            <MaskLine delay={0.45} className="italic text-[#D7263D] pl-[8vw] sm:pl-24">Razenski</MaskLine>
          </h1>
          <div className="overflow-hidden mt-10 max-w-md">
            <motion.p initial={{ y: "100%", opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.75, duration: 1, ease: EASE }}
              className="text-base sm:text-lg text-[#26355A] font-light leading-relaxed">
              Her own room on the internet. Photos, videos and conversations shared only with the people who subscribe.
            </motion.p>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.9, ease: EASE }} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/auth?mode=signup" data-testid="hero-join-cta" className="gold-btn px-8 py-4 text-sm font-semibold tracking-[0.12em] uppercase">Unlock access</Link>
            <button data-testid="hero-tiers-btn" onClick={() => scrollToId("tiers")} className="ghost-btn px-7 py-4 text-sm tracking-[0.12em] uppercase flex items-center gap-2">
              See tiers <ArrowDownRight className="w-4 h-4" />
            </button>
          </motion.div>
          <p className="mt-8 font-mono-x text-xs tracking-[0.18em] text-[#4A5878] uppercase">3 tiers · from $50 / month</p>
        </motion.div>

        <div className="lg:col-span-5 relative" style={{ perspective: 1200 }}>
          <motion.div style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }} className="relative mx-auto w-[78%] sm:w-[60%] lg:w-full max-w-[460px]">
            <motion.div initial={{ clipPath: "inset(100% 0% 0% 0%)" }} animate={{ clipPath: "inset(0% 0% 0% 0%)" }} transition={{ duration: 1.4, delay: 0.35, ease: EASE }}
              className="relative aspect-[3/4] rounded-t-[999px] overflow-hidden ring-1 ring-[#D7263D]/50 shadow-[0_40px_120px_-30px_rgba(215,38,61,0.35)]">
              <motion.img style={{ y: imgY, scale: 1.18 }} src="/img/hero.jpg" alt="Katie Razenski" data-testid="hero-image" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#FFF8EE]/25 via-transparent to-transparent" />
            </motion.div>
            <div style={{ transform: "translateZ(60px)" }} className="absolute -left-8 sm:-left-14 bottom-10"><Badge /></div>
            <div style={{ transform: "translateZ(40px)" }} className="absolute -right-3 sm:-right-6 top-16 bg-[#FFF8EE]/85 backdrop-blur-xl border border-[#D7263D]/30 px-4 py-3">
              <p className="font-mono-x text-[0.65rem] tracking-[0.2em] text-[#D7263D] uppercase">Now posting</p>
              <p className="font-display italic text-lg text-[#0E1B3D]">Members feed</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
